import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import { useCreateCategory, useUpdateCategory, useDeleteCategory, useCategoryQuery } from '../hooks/useQueries';
import { useToast } from '../context/ToastContext';
import IconPicker, { getIconComponent } from '../components/common/IconPicker';
import ConfirmModal from '../components/common/ConfirmModal';
// import ColorPicker from '../components/common/ColorPicker';
import { suggestIconAndColor } from '../utils/categorySuggestions';
import { Sparkles, Trash2, ArrowDownRight, ArrowUpRight } from 'lucide-react';

const initialFormData = {
  name: '',
  type: 'expense',
  color: '#6366f1',
  icon: '🏷️',
  budget: '',
  budgetPeriod: 'monthly'
};

export default function CategoryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'expense';
  const returnTo = searchParams.get('returnTo');

  const { toast } = useToast();
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Fetch category if editing
  const { data: categoryData } = useCategoryQuery(id, { enabled: !!id });
  const category = categoryData?.category || categoryData;

  const [formData, setFormData] = useState({ ...initialFormData, type: initialType });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [hasManuallySetIcon, setHasManuallySetIcon] = useState(false);
  const [hasManuallySetColor, setHasManuallySetColor] = useState(false);

  const isEditing = !!id;

  // Get suggestion based on category name
  const suggestion = useMemo(() => {
    if (!formData.name || formData.name.length < 2) return null;
    return suggestIconAndColor(formData.name, formData.type);
  }, [formData.name, formData.type]);

  const hasSuggestion = useMemo(() => {
    if (!suggestion) return false;
    const iconDifferent = suggestion.icon !== formData.icon;
    const colorDifferent = suggestion.color !== formData.color;
    return iconDifferent || colorDifferent;
  }, [suggestion, formData.icon, formData.color]);

  // Load category data when editing
  useEffect(() => {
    if (category && isEditing) {
      setFormData({
        name: category.name,
        type: category.type,
        color: category.color || '#6366f1',
        icon: category.icon || 'tag',
        budget: category.budget ?? '',
        budgetPeriod: category.budgetPeriod || 'monthly'
      });
      setHasManuallySetIcon(true);
      setHasManuallySetColor(true);
    }
  }, [category, isEditing]);

  // Auto-apply suggestion when name changes
  useEffect(() => {
    if (!isEditing && suggestion && formData.name.length >= 3) {
      setFormData(prev => ({
        ...prev,
        icon: hasManuallySetIcon ? prev.icon : suggestion.icon,
        color: hasManuallySetColor ? prev.color : suggestion.color
      }));
    }
  }, [suggestion, isEditing, hasManuallySetIcon, hasManuallySetColor, formData.name.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleIconChange = (icon) => {
    setFormData((prev) => ({ ...prev, icon }));
    setHasManuallySetIcon(true);
  };

  const handleColorChange = (color) => {
    setFormData((prev) => ({ ...prev, color }));
    setHasManuallySetColor(true);
  };

  const applySuggestion = () => {
    if (suggestion) {
      setFormData((prev) => ({
        ...prev,
        icon: suggestion.icon,
        color: suggestion.color
      }));
      setHasManuallySetIcon(false);
      setHasManuallySetColor(false);
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Category name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 50) {
      errors.name = 'Name cannot exceed 50 characters';
    }

    if (formData.type === 'expense' && formData.budget !== '') {
      const budgetValue = parseFloat(formData.budget);
      if (isNaN(budgetValue) || budgetValue < 0) {
        errors.budget = 'Budget must be a positive number';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        budget: formData.type === 'expense' && formData.budget !== ''
          ? parseFloat(formData.budget)
          : null
      };

      if (formData.type !== 'expense' || formData.budget === '') {
        delete submitData.budgetPeriod;
      }

      if (isEditing) {
        await updateCategoryMutation.mutateAsync({ id, data: submitData });
        toast.success('Category updated successfully');
      } else {
        await createCategoryMutation.mutateAsync(submitData);
        toast.success('Category created successfully');
      }

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/categories');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategoryMutation.mutateAsync(id);
      toast.success(`"${formData.name}" deleted successfully`);
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/categories');
      }
    } catch (err) {
      toast.error('Failed to delete category');
    }
    setDeleteConfirm(false);
  };

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/categories');
    }
  };

  const SuggestedIcon = suggestion ? getIconComponent(suggestion.icon) : null;

  return (
    <FormPageLayout title={isEditing ? 'Edit Category' : 'Add Category'} onBack={handleBack}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Category Type */}
        <div className={`bg-white rounded-xl border border-surface-200 p-1.5 ${isEditing && category?.isDefault ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: 'expense' } })}
              disabled={isEditing && category?.isDefault}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${
                formData.type === 'expense'
                  ? 'bg-expense-100 text-expense-700'
                  : 'text-surface-600 hover:bg-surface-50'
              }`}
            >
              <ArrowDownRight className="w-4 h-4" />
              Expense
            </button>
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'type', value: 'income' } })}
              disabled={isEditing && category?.isDefault}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${
                formData.type === 'income'
                  ? 'bg-income-100 text-income-700'
                  : 'text-surface-600 hover:bg-surface-50'
              }`}
            >
              <ArrowUpRight className="w-4 h-4" />
              Income
            </button>
          </div>
        </div>

        {/* Category Name */}
        <div>
          <label className="label">Category Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Salary, Food & Dining"
            enterKeyHint="done"
            className={`input ${validationErrors.name ? 'input-error' : ''}`}
          />
          {validationErrors.name && (
            <p className="text-expense-600 text-sm mt-1">{validationErrors.name}</p>
          )}
        </div>

        {/* Suggestion Banner */}
        {!isEditing && hasSuggestion && (hasManuallySetIcon || hasManuallySetColor) && (
          <button
            type="button"
            onClick={applySuggestion}
            className="w-full flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-colors text-left"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${suggestion.color}20` }}
            >
              <SuggestedIcon className="w-5 h-5" style={{ color: suggestion.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-700 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                Suggested for "{formData.name}"
              </p>
              <p className="text-xs text-primary-600">Tap to apply</p>
            </div>
          </button>
        )}

        {/* Icon */}
        <div>
          <label className="label">Icon</label>
          <IconPicker
            value={formData.icon}
            onChange={handleIconChange}
            onColorChange={handleColorChange}
            color={formData.color}
          />
        </div>
        {/* <div>
          <label className="label">Color</label>
          <ColorPicker
            value={formData.color}
            onChange={handleColorChange}
          />
        </div> */}

        {/* Budget (Expense only) */}
        {formData.type === 'expense' && (
          <div className="space-y-3 p-3 bg-surface-50 rounded-lg">
            <div>
              <label className="label">Budget (Optional)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500">₹</span>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="10,000"
                  min="0"
                  step="100"
                  className={`input pl-7 ${validationErrors.budget ? 'input-error' : ''}`}
                />
              </div>
              <div className="flex gap-2 mt-2">
                {[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'yearly', label: 'Yearly' }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange({ target: { name: 'budgetPeriod', value: option.value } })}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                      formData.budgetPeriod === option.value
                        ? 'bg-primary-100 text-primary-700 border border-primary-300'
                        : 'bg-white text-surface-600 border border-surface-200 hover:bg-surface-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {validationErrors.budget && (
                <p className="text-expense-600 text-sm mt-1">{validationErrors.budget}</p>
              )}
              <p className="text-xs text-surface-500 mt-1">
                Set a spending limit to track your budget
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={handleBack}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`flex-1 ${formData.type === 'income' ? 'btn-income' : 'btn-expense'}`}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditing ? 'Update' : 'Add Category'}
          </button>
        </div>

        {/* Delete Button (edit mode only) */}
        {isEditing && (
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-expense-600 hover:bg-expense-50 rounded-xl transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Category
          </button>
        )}
      </form>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${formData.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </FormPageLayout>
  );
}
